import { authAxios as axios } from "shared-auth";

import config from "config/global";

import {
  IClientAlertListResponse,
  IInteractionObject,
} from "models/clientAlert";
import {
  IMakeSuggestionsPerformersResponse,
  IMakeSuggestionsPerformersStatsResponse,
  ISuggestion,
} from "models/suggestion";

import {
  fillInteractions,
  getDateSentFrom,
  getDateSentTo,
} from "services/api/suggestions/suggestionsService.helpers";
import {
  DeleteSuggestions,
  EditSuggestions,
  GetClientAlerts,
  GetNudge,
  GetPerformers,
  GetPerformersStats,
  GetSuggestions,
  MakeNudge,
  MakeSuggestions,
  SetClientAlerts,
  SetSuggestions,
} from "services/api/suggestions/suggestionsService.types";
import { IMakeSuggestionsFilterParams } from "models/api";
import _isNumber from "lodash/isNumber";

class SuggestionsService {
  private readonly url: string;

  constructor({ url }: { url: string }) {
    this.url = url;
  }

  public getClientAlerts: GetClientAlerts = async (
    params,
    cancellationToken
  ): Promise<IClientAlertListResponse> => {
    const searchProps = params.search ? `&SenderName=${params.search}` : "";
    const filterProps = `&DateSentFrom=${getDateSentFrom(
      params.filteringOptions?.dateSentFrom
    )}&DateSentTo=${getDateSentTo(params.filteringOptions?.dateSentTo)}`;
    const sortingProps = params.sortOption
      ? `&SortBy=${params.sortOption.name}&SortOrder=${params.sortOption.order}`
      : "";

    const response = await axios(
      `${this.url}/suggestions/myclients/alerts?skip=${params.skip}&take=${params.take}${sortingProps}${filterProps}${searchProps}`,
      {
        method: "get",
        data: params,
        cancelToken: cancellationToken,
      }
    );

    return response.data;
  };

  public getPerformers: GetPerformers = async (
    params,
    roleId,
    cancellationToken
  ): Promise<IMakeSuggestionsPerformersResponse> => {
    const requestString = this.getQueryString(params);

    const response = await axios(
      `${this.url}/suggestions/clientsuggestions/${roleId}?${requestString}`,
      {
        data: params,
        cancelToken: cancellationToken,
      }
    );

    return response.data;
  };

  public getPerformerStats: GetPerformersStats = async (
    params,
    roleId,
    cancellationToken
  ): Promise<IMakeSuggestionsPerformersStatsResponse> => {
    const requestString =
      this.getQueryString(params) + `&newsearchenabled=true`;

    const response = await axios(
      `${this.url}/suggestions/clientsuggestions/${roleId}/filters?${requestString}`,
      {
        method: "get",
        data: params,
        cancelToken: cancellationToken,
      }
    );

    return response.data;
  };

  private getQueryString(params: IMakeSuggestionsFilterParams) {
    const skipProps = `skip=${params.skip}`;
    const takeProps = params.take ? `&take=${params.take}` : "";
    const searchProps = params.textFilter
      ? `&performerName=${params.textFilter}&ExecutedSearch=true`
      : "";
    const genderProps = params.gender
      ? params.gender.map((gender) => `&genders=${gender}`).join("")
      : "";
    const agentsProps = params.agents
      ? params.agents.map((agent) => `&agency=${agent}`).join("")
      : "";
    const whichCVProps = params.whichCV ? `&profiles=${params.whichCV}` : "";
    const playingAgeFromProps = _isNumber(params.playingAgeFrom)
      ? `&playingAge.From=${params.playingAgeFrom}`
      : "";
    const playingAgeToProps = _isNumber(params.playingAgeTo)
      ? `&playingAge.To=${params.playingAgeTo}`
      : "";
    const appearancesProps = params.appearances
      ? params.appearances
          .map((appearance) => `&appearances=${appearance}`)
          .join("")
      : "";
    const locationId = params.locationId?.length
      ? params.locationId.map((id) => `&locationIds=${id}`).join("")
      : "";
    const heritageAndEthnicity =
      params?.heritageAndEthnicity
        ?.map((id) => `&heritageAndEthnicity=${id}`)
        ?.join("") ?? "";
    const heritageAndEthnicityFilterMode =
      params?.heritageAndEthnicityFilterMode
        ? `&heritageAndEthnicityFilterMode=${params.heritageAndEthnicityFilterMode}`
        : "";
    const heritageAndEthnicityInfo = params?.heritageAndEthnicityInfo
      ? `&heritageAndEthnicityInfo=${params.heritageAndEthnicityInfo}`
      : "";

    return `${skipProps}${takeProps}${searchProps}${genderProps}${agentsProps}${whichCVProps}${playingAgeFromProps}${playingAgeToProps}${appearancesProps}${locationId}${heritageAndEthnicity}${heritageAndEthnicityFilterMode}${heritageAndEthnicityInfo}`;
  }

  public getSuggestions: GetSuggestions = async (
    roleId,
    cancellationToken
  ): Promise<ISuggestion[]> => {
    const response = await axios(
      `${this.url}/suggestions/roles/${roleId}/suggestions`,
      {
        method: "get",
        cancelToken: cancellationToken,
      }
    );

    return response.data;
  };

  public getNudge: GetNudge = async (
    breakdownId,
    roleId,
    cancellationToken
  ) => {
    const response = await axios(
      `${this.url}/nudges/breakdowns/${breakdownId}/roles/${roleId}/nudges`,
      {
        method: "get",
        cancelToken: cancellationToken,
      }
    );

    return response.data;
  };

  public makeSuggestions: MakeSuggestions = async (
    breakdownId,
    roleId,
    suggestions,
    cancellationToken
  ) => {
    const data = { created: suggestions };

    return this.setSuggestions(breakdownId, roleId, data, cancellationToken);
  };

  public deleteSuggestions: DeleteSuggestions = async (
    breakdownId,
    roleId,
    performersIds,
    cancellationToken
  ) => {
    const data = { deleted: performersIds };

    return this.setSuggestions(breakdownId, roleId, data, cancellationToken);
  };

  public editSuggestions: EditSuggestions = async (
    breakdownId,
    roleId,
    patch,
    cancellationToken
  ) => {
    const data = { edited: patch };

    return this.setSuggestions(breakdownId, roleId, data, cancellationToken);
  };

  public setSuggestions: SetSuggestions = async (
    breakdownId,
    roleId,
    data,
    cancellationToken
  ) => {
    const response = await axios(
      `${this.url}/suggestions/breakdowns/${breakdownId}/roles/${roleId}/suggestions`,
      {
        method: "post",
        data,
        cancelToken: cancellationToken,
      }
    );

    return response.data;
  };

  public markAsRead: SetClientAlerts = (clientAlertIds) => {
    return this.setActionType(fillInteractions(clientAlertIds, true));
  };

  public markAsUnread: SetClientAlerts = (clientAlertIds) => {
    return this.setActionType(fillInteractions(clientAlertIds, false));
  };

  private async setActionType(interactions: IInteractionObject[]) {
    return axios(`${this.url}/suggestions/myclients/alerts`, {
      method: "post",
      data: interactions,
    });
  }

  public makeNudge: MakeNudge = async (
    breakdownId,
    roleId,
    agencyId,
    cancellationToken
  ) => {
    const response = await axios(
      `${this.url}/nudges/breakdowns/${breakdownId}/roles/${roleId}/${agencyId}`,
      {
        method: "post",
        cancelToken: cancellationToken,
      }
    );

    return response.data;
  };
}

export default new SuggestionsService({
  url: config.suggestionsApiUrl,
});
